/**
 * SEO Score Calculator Utilities
 *
 * Calculate SEO scores for blog posts based on various criteria.
 * Used by the BlogPostEditor to show real-time SEO optimization status.
 */

import type { BlogSEOScore } from '../types';
import { stripHtml, calculateWordCount } from './contentStats';

interface SEOScoreInput {
  title: string;
  metaDescription?: string;
  excerpt?: string;
  content: string;
  featuredImage?: string;
  tags: string[];
  slug: string;
}

/**
 * Calculate SEO score for a blog post
 * Returns both overall percentage score and individual criteria checks
 *
 * Scoring breakdown:
 * - Title length (30-60 chars): 20 points
 * - Meta description (120-160 chars): 20 points
 * - Excerpt length (100+ chars): 15 points
 * - Content length (300+ words): 15 points
 * - Featured image: 10 points
 * - Tags (3+): 10 points
 * - Slug length (60 chars max): 10 points
 *
 * @param input - Blog post data to score
 * @returns SEO score breakdown
 */
export const calculateSEOScore = (input: SEOScoreInput): BlogSEOScore => {
  let score = 0;

  // Title length check (20 points)
  const titleLength = input.title.length >= 30 && input.title.length <= 60;
  if (titleLength) score += 20;

  // Meta description check (20 points)
  const metaDescription = Boolean(
    input.metaDescription &&
    input.metaDescription.length >= 120 &&
    input.metaDescription.length <= 160
  );
  if (metaDescription) score += 20;

  // Excerpt length check (15 points)
  const excerptLength = Boolean(input.excerpt && input.excerpt.length >= 100);
  if (excerptLength) score += 15;

  // Content length check (15 points)
  const plainText = stripHtml(input.content);
  const wordCount = calculateWordCount(plainText);
  const contentLength = wordCount >= 300;
  if (contentLength) score += 15;

  // Featured image check (10 points)
  const featuredImage = Boolean(input.featuredImage);
  if (featuredImage) score += 10;

  // Tags check (10 points)
  const tags = input.tags.length >= 3;
  if (tags) score += 10;

  // Slug length check (10 points)
  const slug = Boolean(input.slug && input.slug.length <= 60);
  if (slug) score += 10;

  return {
    overall: score,
    titleLength,
    metaDescription,
    contentLength,
    featuredImage,
    tags,
    slug,
  };
};

/**
 * Get SEO score indicator color based on score
 * @param score - Overall SEO score (0-100)
 * @returns Tailwind color class
 */
export const getSEOScoreColor = (score: number): string => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
};

/**
 * Get SEO progress bar color based on score
 * @param score - Overall SEO score (0-100)
 * @returns Tailwind background color class
 */
export const getSEOProgressColor = (score: number): string => {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-yellow-500';
  return 'bg-red-500';
};
